import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SideResponse } from "../../codegen-api";
import { PositionHistoryItem } from "../../interfaces/history";
import { ITableColumn } from "../../interfaces/Table/TableColumn";
import { getMarginTypeLabel } from "../../utils/margin";
import PositionHistoryDetailsModal from "./details/PositionHistoryDetailsModal";
import { Direction } from "../shared/Table/style";
import HistoryTableShell from "./HistoryTableShell";
import {
  buildActionsColumn,
  buildMarketColumn,
  buildPriceColumn,
  buildSignedNumberColumn,
  buildSizeColumn,
  buildTimestampColumn,
} from "./columns";
import {
  DecoratedFields,
  useDecoratedHistoryItems,
} from "./useDecoratedHistoryItems";

interface IPositionHistoryTableProps {
  items: PositionHistoryItem[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  height?: number;
}

type DecoratedPosition = PositionHistoryItem & DecoratedFields;

function PositionHistoryTable({
  items,
  isLoading,
  isLoadingMore,
  hasMore,
  onLoadMore,
  height,
}: IPositionHistoryTableProps) {
  const { t } = useTranslation("app", {
    keyPrefix: "components.History.PositionHistoryTable",
  });
  const { t: translateAll } = useTranslation();
  const { t: tooltips } = useTranslation("tooltips");
  const decorated = useDecoratedHistoryItems<PositionHistoryItem>(items);
  const [details, setDetails] = useState<DecoratedPosition>();

  const columns: ITableColumn<DecoratedPosition>[] = useMemo(() => {
    const ctx = { t, tooltips, translateAll };
    return [
      buildMarketColumn<DecoratedPosition>(ctx),
      {
        title: t("side", { defaultValue: "Side" }),
        accessor: "position_side",
        tooltip: tooltips("side"),
        Cell: ({ row }) => {
          const isLong = row.original.position_side === "long";
          const label = row.original.is_liquidated
            ? isLong
              ? t("liquidated_long", { defaultValue: "Liquidated Long" })
              : t("liquidated_short", { defaultValue: "Liquidated Short" })
            : isLong
            ? t("long", { defaultValue: "Long" })
            : t("short", { defaultValue: "Short" });
          return (
            <Direction side={isLong ? SideResponse.Buy : SideResponse.Sell}>
              {label}
            </Direction>
          );
        },
      },
      {
        title: t("margin_type", { defaultValue: "Margin" }),
        accessor: "margin_type",
        tooltip: tooltips("marginType", {
          defaultValue:
            "Margin mode for the position (Cross uses portfolio collateral, Isolated uses position-specific collateral).",
        }),
        Cell: ({ value }) => getMarginTypeLabel(value),
      },
      {
        title: t("fills", { defaultValue: "Fills" }),
        accessor: "open_fills",
        align: "right",
        tooltip: tooltips("fills", {
          defaultValue:
            "Number of fills used to open and close the position (open / close).",
        }),
        Cell: ({ row }) => {
          const open = row.original.open_fills;
          const close = row.original.close_fills;
          if (open === undefined && close === undefined) return "-";
          return `${open ?? 0} / ${close ?? 0}`;
        },
      },
      buildSizeColumn<DecoratedPosition>(ctx, {
        priceAccessor: "avg_entry_price",
      }),
      buildPriceColumn<DecoratedPosition>(ctx, {
        accessor: "avg_entry_price",
        titleKey: "entry_price",
      }),
      buildPriceColumn<DecoratedPosition>(ctx, {
        accessor: "avg_exit_price",
        titleKey: "exit_price",
      }),
      buildSignedNumberColumn<DecoratedPosition>(ctx, {
        accessor: "gross_pnl",
        titleKey: "gross_pnl",
        tooltipKey: "realizedPnl",
      }),
      buildSignedNumberColumn<DecoratedPosition>(ctx, {
        accessor: "total_fees",
        titleKey: "fees",
        tooltipKey: "fee",
        invert: true,
      }),
      buildSignedNumberColumn<DecoratedPosition>(ctx, {
        accessor: "net_pnl",
        titleKey: "net_pnl",
        tooltipKey: "realizedPnl",
      }),
      buildTimestampColumn<DecoratedPosition>(ctx, {
        accessor: "closed_timestamp",
        titleKey: "closed",
        unit: "nanos",
      }),
      buildActionsColumn<DecoratedPosition>("position_key", (row) =>
        setDetails(row)
      ),
    ];
  }, [t, tooltips, translateAll]);

  return (
    <>
      <PositionHistoryDetailsModal
        position={details}
        onHide={() => setDetails(undefined)}
      />
      <HistoryTableShell<DecoratedPosition>
        columns={columns}
        items={decorated}
        isLoading={isLoading}
        isLoadingMore={isLoadingMore}
        hasMore={hasMore}
        onLoadMore={onLoadMore}
        onRowClick={(row) => setDetails(row)}
        emptyText={t("no_position_history", {
          defaultValue: "No position history yet.",
        })}
        height={height}
      />
    </>
  );
}

export default PositionHistoryTable;
